import { create } from 'zustand';

import type { Session } from './types';

interface SessionState {
  readonly session: Session | null;

  /** False until the boot-time refresh attempt has settled. */
  readonly isRestoring: boolean;

  setSession: (session: Session | null) => void;
  setRestored: () => void;
  clearPasswordChangeRequirement: () => void;
}

/**
 * The signed-in session.
 *
 * **The access token lives here and nowhere else — never `localStorage`.** An XSS on the
 * admin page can read `localStorage` trivially; it cannot read a closure. The refresh
 * token is an httpOnly cookie the JavaScript never sees at all, which is what makes a
 * page reload recoverable despite the access token being lost with the tab.
 *
 * `isRestoring` exists so the router does not bounce a signed-in owner to the login page
 * during the boot-time refresh. Without it, every page reload flashes the login form.
 */
export const useSessionStore = create<SessionState>((set) => ({
  session: null,
  isRestoring: true,

  setSession: (session) => {
    set({ session });
  },

  setRestored: () => {
    set({ isRestoring: false });
  },

  clearPasswordChangeRequirement: () => {
    set((state) =>
      state.session === null
        ? state
        : { session: { ...state.session, mustChangePassword: false } },
    );
  },
}));

/** Reads the current token outside React. Used by the API client's request interceptor. */
export function currentAccessToken(): string | null {
  return useSessionStore.getState().session?.accessToken ?? null;
}
